import { createContext, useState, useContext, useEffect } from "react";
import { client2 } from "../../operationalRequest";

interface Notification {
    id: string;
    message: string;
    timestamp: string;
    status: string; // unread or read
    data: { title: string };
    created_at: string;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number; // Track unread count
    fetchNotifications: () => void;
    markAsRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotifications must be used within a NotificationProvider");
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0); // Initialize unread count

    const fetchNotifications = async () => {
        try {
            const response = await client2.get(`api/notifications?page=1&pageSize=10&sortOrder=desc&sortProperty=created_at`);
            const fetchedNotifications: Notification[] = response.data.data.notifications;

            setNotifications(fetchedNotifications);

            // Update unread count
            setUnreadCount(fetchedNotifications.filter((n) => n.status === "unread").length);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    };

    const markAsRead = async (id: string) => {
        try {
            await client2.put(`api/notifications/${id}`, { notification: { status: "read" } });

            // Update notifications list and unread count
            setNotifications((prevNotifications) =>
                prevNotifications.map((n) =>
                    n.id === id ? { ...n, status: "read" } : n
                )
            );

            // Recalculate unread count
            setUnreadCount((prevCount) =>
                prevCount > 0 ? prevCount - 1 : 0
            );
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, fetchNotifications, markAsRead }}>
            {children}
        </NotificationContext.Provider>
    );
};
